import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MobXProviderContext } from 'mobx-react';
import { queryKeys } from '@portal/ui/hooks/queryKeys';

interface Benefits {
  /** The member's account-credit balance, in the space's currency. */
  CurrentBalance?: number;
}

/**
 * Fetch the member's account-credit balance (`CurrentBalance` from
 * `/allowances/plans`, the same payload as My Plan → Benefits).
 *
 * The basket uses this to predict the account credit Nexudus will apply at
 * purchase time: `/basket/PreviewInvoice` never reports account credit, so
 * without the balance the basket total disagrees with the payment page for
 * anyone holding credit. Always fetched fresh rather than read off the store —
 * a stale balance is the main source of a wrong prediction.
 *
 * IMPORTANT: Components using this hook must be wrapped with MobX `observer()`.
 */
export function useAccountBalance(enabled = true) {
  const stores = useContext(MobXProviderContext) as Record<string, unknown>;

  if (!stores?.invoicingStore) {
    throw new Error('useAccountBalance must be used within a MobX Provider');
  }

  const store = stores.invoicingStore as {
    loadBenefits?: () => Promise<Benefits>;
  };

  const canFetch = typeof store.loadBenefits === 'function';

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.accountBalance,
    queryFn: async () => {
      const benefits = await store.loadBenefits!();
      return Math.max(0, benefits?.CurrentBalance ?? 0);
    },
    enabled: enabled && canFetch,
    // Short-lived: credit can be spent from another device or adjusted by an
    // admin, and a stale figure shows the user a total they won't be charged.
    staleTime: 30 * 1000,
    gcTime: 30 * 1000,
  });

  return {
    accountBalance: data ?? 0,
    isLoading,
  };
}
